import {
  useState, useEffect 
} from 'react';
import {
  X, Save 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/classnames';
import type { Recipe } from '../../types';
import type {
  RecipeFormData, TabId 
} from './form-types';
import { emptyFormData } from './form-types';
import { BasicInfoTab } from './BasicInfoTab';
import { IngredientsTab } from './IngredientsTab';
import { InstructionsTab } from './InstructionsTab';
import { MetadataTab } from './MetadataTab';

interface RecipeFormModalProps {
  readonly recipe?: Recipe | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave: (data: RecipeFormData) => void;
  readonly allRecipes?: Recipe[];
}

export type { RecipeFormData } from './form-types';

export function RecipeFormModal({
  recipe,
  isOpen,
  onClose,
  onSave,
  allRecipes = [],
}: RecipeFormModalProps) {
  const [formData, setFormData] = useState<RecipeFormData>(emptyFormData);
  const [activeTab, setActiveTab] = useState<TabId>('basic');

  const isEditing = Boolean(recipe);

  useEffect(() => {
    if (recipe) {
      setFormData({
        name: recipe.name,
        description: recipe.description ?? '',
        type: recipe.type,
        category: recipe.category,
        cuisine: recipe.cuisine ?? '',
        servings: recipe.servings,
        servingSize: recipe.servingSize ?? '',
        prepTime: recipe.prepTime ?? 0,
        cookTime: recipe.cookTime ?? 0,
        yield: recipe.yield ?? recipe.servings,
        yieldUnit: recipe.yieldUnit ?? 'portions',
        difficulty: recipe.difficulty ?? 'medium',
        ingredients: [...recipe.ingredients],
        subRecipeIds: recipe.subRecipeIds ?? [],
        instructions: recipe.instructions?.length ? [...recipe.instructions] : [''],
        allergens: [...recipe.allergens],
        dietaryFlags: [...recipe.dietaryFlags],
      });
    } else {
      setFormData(emptyFormData);
    }
    setActiveTab('basic');
  }, [recipe, isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(formData);
  };

  const updateField = <K extends keyof RecipeFormData>(field: K, value: RecipeFormData[K]) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value 
    }));
  };

  const toggleArrayItem = (field: 'allergens' | 'dietaryFlags' | 'subRecipeIds', value: string) => {
    const current = formData[field];
    if (current.includes(value)) {
      updateField(field, current.filter((v) => v !== value));
    } else {
      updateField(field, [...current, value]);
    }
  };

  const tabs = [
    {
      id: 'basic',
      label: 'Basic Info' 
    },
    {
      id: 'ingredients',
      label: `Ingredients (${formData.ingredients.length})` 
    },
    {
      id: 'instructions',
      label: `Instructions (${formData.instructions.filter(Boolean).length})` 
    },
    {
      id: 'metadata',
      label: 'Allergens & Diet' 
    },
  ] as const;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-4xl h-[700px] max-h-[90vh] flex flex-col rounded-lg bg-background shadow-xl">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold">
              {isEditing ? 'Edit Recipe' : 'Create New Recipe'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isEditing ? `Editing: ${recipe?.name}` : 'Fill in the recipe details'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 hover:bg-muted transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 border-b px-6">
          <nav className="flex gap-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'border-b-2 px-1 py-3 text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'basic' && (
            <BasicInfoTab
              formData={formData}
              updateField={updateField}
              toggleArrayItem={toggleArrayItem}
              allRecipes={allRecipes}
              currentRecipeId={recipe?.recipeId}
            />
          )}
          {activeTab === 'ingredients' && (
            <IngredientsTab formData={formData} updateField={updateField} />
          )}
          {activeTab === 'instructions' && (
            <InstructionsTab formData={formData} updateField={updateField} />
          )}
          {activeTab === 'metadata' && (
            <MetadataTab formData={formData} toggleArrayItem={toggleArrayItem} />
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between border-t px-6 py-4">
          <div className="text-sm text-muted-foreground">
            {formData.ingredients.length} ingredients · {formData.instructions.filter(Boolean).length} steps
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!formData.name || !formData.category}>
              <Save className="mr-2 h-4 w-4" />
              {isEditing ? 'Update Recipe' : 'Create Recipe'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
